import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  FileText,
  Filter,
  Archive,
  Eye,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import blogService from '@/services/blogService';
import { cn } from '@/lib/utils';

/**
 * Blog durum badge bileşeni
 */
function BlogStatusBadge({ status }) {
  const variants = {
    draft: { label: 'Taslak', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    review: { label: 'İncelemede', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    published: { label: 'Yayında', className: 'bg-green-100 text-green-700 border-green-200' },
    archived: { label: 'Arşivlenmiş', className: 'bg-red-100 text-red-700 border-red-200' },
  };
  const config = variants[status] || variants.draft;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

/**
 * Tarih formatlama
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Kategori badge bileşeni
 */
function CategoryBadge({ category }) {
  if (!category) return <span className="text-muted-foreground">-</span>;
  return (
    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
      {category}
    </Badge>
  );
}

/**
 * Tablo skeleton loader
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-12 w-20 rounded" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-48" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-16" />
          </TableCell>
          <TableCell>
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * StaffBlogPage - Blog yönetimi listesi sayfası
 */
const StaffBlogPage = () => {
  const { toast } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [publishing, setPublishing] = useState(null);
  
  // Filtreler
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Kategoriler
  const [categories, setCategories] = useState([]);

  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await blogService.getBlogPosts({
        limit: 100,
      });
      
      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Blog yazıları yüklenirken bir hata oluştu.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await blogService.getCategories();
      if (!error && data) {
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Filtrelenmiş ve sıralanmış bloglar
  const filteredBlogs = useMemo(() => {
    let filtered = [...blogs];

    // Arama filtresi
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.title?.toLowerCase().includes(query) ||
          b.title_tr?.toLowerCase().includes(query) ||
          b.title_en?.toLowerCase().includes(query)
      );
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    // Kategori filtresi
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((b) => b.category === categoryFilter);
    }

    // Sıralama
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'az':
          return (a.title_tr || a.title || '').localeCompare(b.title_tr || b.title || '', 'tr');
        case 'za':
          return (b.title_tr || b.title || '').localeCompare(a.title_tr || a.title || '', 'tr');
        default:
          return 0;
      }
    });

    return filtered;
  }, [blogs, searchQuery, statusFilter, categoryFilter, sortBy]);

  // Sayfalama
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!blogToDelete) return;

    try {
      const { error } = await blogService.deleteBlogPost(blogToDelete.id);
      if (error) throw error;
      
      toast({
        title: 'Başarılı',
        description: 'Blog yazısı başarıyla silindi.',
      });
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Blog yazısı silinirken bir hata oluştu.',
      });
    } finally {
      setDeleteDialogOpen(false);
      setBlogToDelete(null);
    }
  };

  const handleTogglePublish = async (blog) => {
    try {
      setPublishing(blog.id);
      
      const { error } = blog.status === 'published'
        ? await blogService.unpublishBlogPost(blog.id)
        : await blogService.publishBlogPost(blog.id);
        
      if (error) throw error;
      
      toast({
        title: 'Başarılı',
        description: blog.status === 'published'
          ? 'Blog yazısı taslağa çevrildi.'
          : 'Blog yazısı başarıyla yayınlandı.',
      });
      fetchBlogs();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Durum değiştirilirken bir hata oluştu.',
      });
    } finally {
      setPublishing(null);
    }
  };

  const handleArchive = async (blog) => {
    try {
      setPublishing(blog.id);
      
      const { error } = await blogService.archiveBlogPost(blog.id);
      if (error) throw error;
      
      toast({
        title: 'Başarılı',
        description: 'Blog yazısı arşivlendi.',
      });
      fetchBlogs();
    } catch (error) {
      console.error('Error archiving blog:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Arşivleme işlemi sırasında bir hata oluştu.',
      });
    } finally {
      setPublishing(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Blog Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Toplam {filteredBlogs.length} blog yazısı
          </p>
        </div>
        <Link to="/staff/blog/new">
          <Button className="w-full sm:w-auto">
            <Plus size={18} className="mr-2" />
            Yeni Yazı Ekle
          </Button>
        </Link>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Başlık ile ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Durum Filtresi */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="draft">Taslak</SelectItem>
                <SelectItem value="review">İncelemede</SelectItem>
                <SelectItem value="published">Yayında</SelectItem>
                <SelectItem value="archived">Arşivlenmiş</SelectItem>
              </SelectContent>
            </Select>

            {/* Kategori Filtresi */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sıralama */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sırala" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni</SelectItem>
                <SelectItem value="oldest">En Eski</SelectItem>
                <SelectItem value="az">A-Z</SelectItem>
                <SelectItem value="za">Z-A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Blog Tablosu */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Görsel</TableHead>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Yazar</TableHead>
                  <TableHead>Yayın Tarihi</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableSkeleton />
                ) : paginatedBlogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <FileText size={48} className="mb-3 opacity-20" />
                        <p className="text-lg font-medium mb-2">
                          {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                            ? 'Sonuç bulunamadı'
                            : 'Henüz blog yazısı eklenmemiş'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && categoryFilter === 'all' && (
                          <Link to="/staff/blog/new">
                            <Button variant="outline" className="mt-4">
                              <Plus size={18} className="mr-2" />
                              İlk Yazıyı Ekle
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBlogs.map((blog) => (
                    <TableRow key={blog.id}>
                      <TableCell>
                        <div className="w-20 h-12 rounded overflow-hidden bg-gray-100">
                          {blog.featured_image ? (
                            <img
                              src={blog.featured_image}
                              alt={blog.title_tr || blog.title || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <Eye size={16} />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-foreground truncate">
                            {blog.title_tr || blog.title || '-'}
                          </p>
                          {blog.excerpt && (
                            <p className="text-sm text-muted-foreground truncate">
                              {blog.excerpt}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <CategoryBadge category={blog.category} />
                      </TableCell>
                      <TableCell>
                        <BlogStatusBadge status={blog.status} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {blog.author?.full_name || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(blog.published_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/staff/blog/${blog.id}`}>
                            <Button variant="outline" size="icon" title="Düzenle">
                              <Edit size={16} />
                            </Button>
                          </Link>
                          
                          {blog.status === 'published' ? (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleTogglePublish(blog)}
                              disabled={publishing === blog.id}
                              title="Taslğa Çevir"
                            >
                              {publishing === blog.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                              ) : (
                                <Archive size={16} />
                              )}
                            </Button>
                          ) : blog.status === 'archived' ? null : (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleTogglePublish(blog)}
                              disabled={publishing === blog.id}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Yayınla"
                            >
                              {publishing === blog.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                              ) : (
                                <Eye size={16} />
                              )}
                            </Button>
                          )}
                          
                          {blog.status !== 'archived' && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleArchive(blog)}
                              disabled={publishing === blog.id}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Arşivle"
                            >
                              <Archive size={16} />
                            </Button>
                          )}
                          
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDeleteClick(blog)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Önceki
          </Button>
          <div className="flex items-center gap-1">
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page}>...</span>;
              }
              return null;
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Sonraki
          </Button>
        </div>
      )}

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Blog Yazısını Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Blog yazısı kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>{blogToDelete?.title_tr || blogToDelete?.title}</strong> başlıklı
              yazıyı silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffBlogPage;
